import { type Service, type ServicePlaceholder } from "@prisma/client";
import { Button, Input, Textarea } from "@workspace/ui/components";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@workspace/ui/components/accordion";
import { InputTags } from "@workspace/ui/components/input-tags";
import { InfoIcon } from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "~/utils/api";
import { TooltipWrapper } from "../TooltipWrapper";

export const DevPanelTemplateGalleryPlaceholders = () => {
  const [templateServices] = api.service.getTemplateServices.useSuspenseQuery();
  const placeholderTemplateServices = templateServices.filter(
    (s) => s.placeholder != null
  );
  const [searchQuery] = useState("");
  const [expandedItems, setExpandedItems] = useState<string[]>([]);

  // Filter placeholder templates based on search query
  const filteredPlaceholders = placeholderTemplateServices.filter((service) => {
    if (!searchQuery) return true;

    const search = searchQuery.toLowerCase();
    const nameMatch = service.name.toLowerCase().includes(search);
    const descMatch =
      service.descriptionShort?.toLowerCase()?.includes(search) || false;
    const tagMatch = service.tags.some((tag) =>
      tag.toLowerCase().includes(search)
    );

    return nameMatch || descMatch || tagMatch;
  });

  const handleAccordionChange = (value: string) => {
    if (value) {
      setExpandedItems([value]);
    } else {
      setExpandedItems([]);
    }
  };

  if (placeholderTemplateServices.length === 0) {
    return (
      <div className="flex h-40 items-center justify-center text-gray-500">
        No placeholder templates found
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-gray-500">
            Count: {placeholderTemplateServices.length}
          </p>
        </div>
      </div>

      <Accordion
        type="single"
        collapsible
        className="w-full"
        value={expandedItems[0]}
        onValueChange={handleAccordionChange}
      >
        {filteredPlaceholders.length === 0 ? (
          <div className="flex h-20 items-center justify-center text-gray-500">
            No templates match your search
          </div>
        ) : (
          filteredPlaceholders.map((placeholderService) => (
            <AccordionItem
              key={placeholderService.id}
              value={placeholderService.id}
            >
              <AccordionTrigger className="flex justify-between px-4 py-2 hover:no-underline">
                <div className="flex items-center gap-3">
                  <span>{placeholderService.name}</span>
                </div>
              </AccordionTrigger>
              <AccordionContent className="px-4 py-2">
                <PlaceholderTemplateForm service={placeholderService} />
              </AccordionContent>
            </AccordionItem>
          ))
        )}
      </Accordion>
    </div>
  );
};

type PlaceholderTemplateService = Service & {
  placeholder: ServicePlaceholder | null;
  activeImplementation: { envVariables: Record<string, string> } | null;
  tags: string[];
};

interface PlaceholderTemplateFormProps {
  service: PlaceholderTemplateService;
}

const PlaceholderTemplateForm = ({ service }: PlaceholderTemplateFormProps) => {
  const utils = api.useUtils();
  const editServiceMutation = api.service.editService.useMutation({
    onSuccess: async () => {
      await utils.service.getTemplateServices.invalidate();
      setHasChanges(false);
      toast.success(`${service.name} has been successfully updated.`);
    },
    onError: (error) => {
      toast.error(`Error updating template: ${error.message}`);
    },
  });

  // Create state for the editable fields
  const [name, setName] = useState(service.name);
  const [descriptionShort, setDescriptionShort] = useState(
    service.descriptionShort || ""
  );
  const [tags, setTags] = useState<string[]>(service.tags);
  const [hasChanges, setHasChanges] = useState(false);

  // Check for changes in fields
  useEffect(() => {
    const isChanged =
      name !== service.name ||
      descriptionShort !== (service.descriptionShort || "") ||
      JSON.stringify(tags) !==
        JSON.stringify(
          Array.isArray(service.tags)
            ? service.tags
            : JSON.parse((service.tags as string) || "[]")
        );

    setHasChanges(isChanged);
  }, [name, descriptionShort, tags, service]);

  const handleSave = () => {
    editServiceMutation.mutate({
      id: service.id,
      data: {
        name,
        descriptionShort,
        tags,
      },
    });
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <Button
          size="sm"
          className="rounded-md text-xs"
          disabled={!hasChanges || editServiceMutation.isPending}
          onClick={handleSave}
        >
          {editServiceMutation.isPending ? "Saving..." : "Save"}
        </Button>
      </div>

      <div className="space-y-4">
        <div className="space-y-2">
          <label className="text-sm font-medium">Template Name</label>
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Template Name"
          />
        </div>

        <div className="space-y-2">
          <div className="flex items-center gap-1">
            <label className="text-sm font-medium">Short Description </label>
            <TooltipWrapper
              className="max-w-80"
              content="Markdown is not supported. This is used to display a concise description for the template gallery page."
            >
              <InfoIcon size={14} className="text-gray-500" />
            </TooltipWrapper>
          </div>
          <Textarea
            value={descriptionShort}
            onChange={(e) => setDescriptionShort(e.target.value)}
            placeholder="Short Description"
            rows={2}
          />
        </div>

        <div className="space-y-2">
          <div className="flex items-center gap-1">
            <label className="text-sm font-medium">Tags</label>
            <TooltipWrapper
              className="max-w-80"
              content="Tags need to be written with case and space-sensivity. E.g. 'SEO' instead of 'seo', 'Web Scraping' instead of 'web-scraping'."
            >
              <InfoIcon size={14} className="text-gray-500" />
            </TooltipWrapper>
          </div>
          <InputTags value={tags} onChange={setTags} placeholder="Add tags" />
        </div>

        <div className="space-y-2">
          <label className="text-sm font-medium">Service ID</label>
          <Input value={service.id} disabled />
        </div>
      </div>
    </div>
  );
};
